import React, { useEffect, useState } from 'react';
import ksBridge from '@ks/ks-bridge';
import { useHistory } from 'react-router-dom';
import { RootState, useRematchState } from '@/store';
import skin from '@/services/skin';
import location from '@/services/location';
import { IMenu } from '@/schemas/operation';
import { openOrDownloadUrl } from '@/utils/open-or-download-url';
import { className, isInYoda, sendClick, sendShow } from '@utils/helpers';
import { getTargetUrl, parseQuery } from '@utils/url';
import { Log } from '@aio/utils';
import loadable from '@loadable/component';
import * as services from '@services/index';
import styles from './index.module.scss';

const ThirdPartyServicePopup = loadable(() =>
  import('../../components/ThirdPartyServicePopup')
);

const mapState = (state: RootState) => ({
  isLogin: state.user.isLogin,
  menus: state.home.menus,
  serviceMenus: state.home.serviceMenus,
  defaultCoordinate: state.register.defaultCoordinate
});

const Menu = () => {
  const { menus, serviceMenus } = useRematchState(mapState);
  const { isIOS } = ksBridge.sniff;
  const [
    thirdPartyServicePopupVisible,
    setThirdPartyServicePopupVisible
  ] = useState(false);
  const history = useHistory();

  const handleMenuClick = async (menu: IMenu) => {
    let { featureUrl } = menu;
    const { externalLink, featureNameIndex } = menu;
    sendClick('TOP_FUNCTION_LIST', {
      button_name: featureNameIndex
    });

    if (featureUrl === '/skin-detection') {
      const result = await skin.getLatelySkinResult();
      if (result) {
        featureUrl = '/skin-detection/report';
      }
    }
    if (externalLink) {
      const url = getTargetUrl(featureUrl);
      openOrDownloadUrl(url);
    } else if (isInYoda) {
      const url = getTargetUrl(
        `${window.location.origin}${featureUrl}${
          featureUrl.includes('?') ? '&' : '?'
        }layoutType=4&enableWK=1&hyId=healthy-channel`
      );
      openOrDownloadUrl(url);
    } else {
      history.push(getTargetUrl(featureUrl));
    }
  };

  useEffect(() => {
    if (
      serviceMenus.find((menu) => menu.featureUrl.includes('/quick-consult'))
    ) {
      const { from } = parseQuery('');
      sendShow('QUICK_CONSULT_BTN', {
        from,
        show_doctor_list: ''
      });
    }
  }, [serviceMenus]);

  const jumpKrnUrl = async (featureName: string) => {
    const entrances = await services.entrances.getEntrances();
    const url =
      entrances?.find((item: any) => item.featureName === featureName)
        ?.krnUrl || '';
    openOrDownloadUrl(url);
  };
  const handleServerClick = (menu: IMenu) => {
    const { externalLink, featureUrl, krnUrl, featureName } = menu;
    if (featureUrl === '/quick-consult/create?prodType=quickConsult') {
      const { from } = parseQuery('');
      sendClick('QUICK_CONSULT_BTN', {
        from,
        show_doctor_list: ''
      });
      setThirdPartyServicePopupVisible(true);
      return;
    }
    if (featureUrl === '/register/home') {
      processRegisterEntry();
      return;
    }

    if (externalLink) {
      const url = getTargetUrl(featureUrl);
      openOrDownloadUrl(url);
    } else if (isInYoda) {
      if (krnUrl) {
        jumpKrnUrl(featureName);
      } else {
        const url = getTargetUrl(
          `${window.location.origin}${featureUrl}${
            featureUrl.includes('?') ? '&' : '?'
          }layoutType=4&enableWK=1&hyId=healthy-channel`
        );
        openOrDownloadUrl(url);
      }
    } else {
      !krnUrl && history.push(getTargetUrl(featureUrl));
    }
  };

  // 挂号入口相关判断处理
  const processRegisterEntry = async () => {
    try {
      const {
        code,
        name,
        registerEnable,
        existHospital
      } = await location.registerLocation();
      if (registerEnable) {
        localStorage.setItem(
          'cityInfo',
          JSON.stringify({
            cityCode: code,
            cityName: name,
            areaCode: '-1',
            areaName: '不限',
            enableRegister: registerEnable
          })
        );
        handleGoRegisterPage();
      } else if (existHospital) {
        localStorage.setItem(
          'cityInfo',
          JSON.stringify({
            cityCode: code,
            cityName: name,
            areaCode: '-1',
            areaName: '不限',
            enableRegister: false
          })
        );
        handleGoRegisterSearchPage();
      } else {
        handleGoRegisterFallbackPage();
      }
    } catch (error) {
      Log.error(error);
      handleGoRegisterFallbackPage();
    }
  };

  const handleGoRegisterPage = async () => {
    // 在白名单中的地区才可访问挂号首页
    const registerPath = `/register/home?layoutType=4&enableWK=1&hyId=healthy-channel`;
    const url = getTargetUrl(`${window.location.origin}${registerPath}`);
    openOrDownloadUrl(url);
  };

  const handleGoRegisterSearchPage = () => {
    const registerPath = `/register/search/hospital?layoutType=4&enableWK=1&hyId=healthy-channel&title=挂号指引`;
    const url = getTargetUrl(`${window.location.origin}${registerPath}`);
    openOrDownloadUrl(url);
  };

  const handleGoRegisterFallbackPage = async () => {
    const registerPath = `/register/fallback?layoutType=4&enableWK=1&hyId=healthy-channel`;
    const url = getTargetUrl(`${window.location.origin}${registerPath}`);
    openOrDownloadUrl(url);
  };

  return (
    <div
      className={className(
        styles['menu-wrapper'],
        isIOS ? styles['menu-ios'] : ''
      )}
    >
      <div className={styles['menu-top']}>
        {serviceMenus.map((menu, index) => (
          <div
            aria-hidden
            key={menu.featureName}
            className={styles['menu-single']}
            onClick={() => handleServerClick(menu)}
          >
            <div className={styles['icon-wrapper']}>
              <img src={menu.featurePic} alt="" />
              {menu.featureSubscript && <i>{String(menu.featureSubscript)}</i>}
            </div>
            <h3>{menu.featureName}</h3>
          </div>
        ))}
      </div>
      {!isIOS && (
        <div className={styles.menu}>
          <ul>
            {menus.map((menu) => (
              <li
                key={menu.featureName}
                aria-hidden
                onClick={() => handleMenuClick(menu)}
                className={menus.length > 5 ? styles.more : ''}
              >
                <div className={styles.icon}>
                  <img src={menu.featurePic} alt="" />
                  {menu.featureSubscript && (
                    <i>{String(menu.featureSubscript)}</i>
                  )}
                </div>
                <p>{menu.featureName}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      <ThirdPartyServicePopup
        visible={thirdPartyServicePopupVisible}
        onClose={() => setThirdPartyServicePopupVisible(false)}
      />
    </div>
  );
};
export default Menu;
